/* eslint-disable no-case-declarations */
import { useEffect, useState } from "react";
import PageLoader from "../../../components/PageLoader";
import { toast } from "react-toastify";
import { httprequest } from "../../../data/api";
import { useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import Layout from "../../../components/Layout";
import Table from "../../../components/Table/Table";
import { TableSearch } from "../../../components/Table/TableActions";
import { Icon } from "@iconify/react/dist/iconify.js";
import BreadCrumb from "../../../components/BreadCrumb";
import Badge from "../../../components/Badge";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import { generateReportPDF } from "../../../components/PdfGenerator/pdfGenerator";
import { useUpload } from "../../../hooks/useUpload";

const REPORT_ENDPOINT = "/api/v1/custom-reports";
const MARK_REPORT_ENDPOINT = "/api/v1/custom-reports/";

const CustomReport = () => {
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [reportData, setReportData] = useState([]);
  const navigate = useNavigate();
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [reports, setReports] = useState([]);

  // State variables for modals and actions
  const [selectedRows, setSelectedRows] = useState([]);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [batchAction, setBatchAction] = useState(null);
  const [batchPrintModalOpen, setBatchPrintModalOpen] = useState(false);
  const [batchPrintUrls, setBatchPrintUrls] = useState([]);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [reportToPublish, setReportToPublish] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  const handleViewReport = (row) => {
    if (row?.report_id) {
      navigate(`/report/custom/view/${row.report_id}`);
    } else {
      navigate(`/report/custom/view/${reports?.id}/${row?.student_id}`);
    }
  };

  const getAllReports = () => {
    setIsReportLoading(true);
    httprequest
      .get(`${REPORT_ENDPOINT}/${id}/students`)
      .then((res) => {
        const report = res?.data?.data;

        const rows = (report?.targets || []).map((t) => ({
          id: report.id,
          target_name: `${t.last_name} ${t.first_name}`,
          class_name: report.class?.name,
          status: t.status,
          student_id: t.student_id,
          report_id: t.report_id,
          last_name: t.last_name,
          first_name: t.first_name
        }));

        setReportData(rows);
        setFilteredData(rows);
        setReports(report);
      })
      .catch((err) => {
        toast.error(err?.response?.message);
      })
      .finally(() => {
        setIsReportLoading(false);
      });
  };

  // Upload file function
  const uploadFile = async (fileBlob, fileName) => {
    try {
      const file = new File([fileBlob], fileName, { type: "application/pdf" });
      const response = await useUpload(file);

      return response.data;
    } catch (error) {
      throw new Error(`Upload failed: ${error.response?.data?.message || error.message}`);
    }
  };

  // Individual Publish Functions
  const handlePublishReport = async () => {
    if (!reportToPublish?.report_id) {
      toast.error("No report selected for publishing");

      return;
    }

    setIsPublishing(true);
    try {
      const pdfBlob = await generateReportPDF(reportToPublish.report_id);

      // Upload PDF
      const documentData = await uploadFile(
        pdfBlob,
        `${reportToPublish.last_name}_${reportToPublish.first_name}_report.pdf`
      );

      // Handle both array and object responses
      let document;
      if (Array.isArray(documentData)) {
        document = documentData[0];
      } else {
        document = documentData;
      }

      if (!document || !document.upload_id) {
        throw new Error("Invalid document data received from upload");
      }

      const response = await httprequest.post("/api/v1/custom-reports/publish", {
        reports: [{
          report_id: reportToPublish.report_id,
          document: {
            upload_id: document.upload_id,
            file_url: document.file_url,
            name: `${reportToPublish.last_name} ${reportToPublish.first_name} Report`
          }
        }]
      });

      toast.success(response.data.message || "Report published successfully");
      setPublishModalOpen(false);
      setReportToPublish(null);
      await getAllReports();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Publish error:", error);
      toast.error(`Failed to publish report: ${error.response?.data?.message || error.message || "Unknown error"}`);
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublishClick = (report) => {
    setReportToPublish(report);
    setPublishModalOpen(true);
  };

  // Individual Print Functions
  const handlePrintClick = async (report) => {
    try {
      setSelectedReport(report);
      setPrintModalOpen(true);

      const pdfBlob = await generateReportPDF(report.report_id);
      const pdfUrl = URL.createObjectURL(pdfBlob);

      setSelectedReport(prev => ({ ...prev, pdfUrl }));
    } catch (error) {
      toast.error(`Failed to generate PDF: ${error.message}`);
      setPrintModalOpen(false);
    }
  };

  // Batch Action Functions
  const handleBatchActionChange = (e) => {
    const action = e.target.value;
    if (action && selectedRows.length > 0) {
      setBatchAction(action);
      setIsBatchModalOpen(true);
    } else if (action && selectedRows.length === 0) {
      toast.warning("Please select at least one report");
    }
  };

  const uploadAndPublishReport = async (reports) => {
    try {
      const pdfGenerationPromises = reports.map(async (report) => {
        try {
          const pdfBlob = await generateReportPDF(report.report_id);

          return {
            report,
            pdfBlob,
            studentName: `${report.last_name} ${report.first_name}`
          };
        } catch (error) {
          return {
            report,
            error: `PDF generation failed: ${error.message}`
          };
        }
      });

      const pdfResults = await Promise.all(pdfGenerationPromises);

      const uploadPromises = pdfResults
        .filter(result => result.pdfBlob)
        .map(async ({ report, pdfBlob, studentName }) => {
          try {
            const documentData = await uploadFile(
              pdfBlob,
              `${studentName.replace(/\s+/g, "_")}_report.pdf`
            );
            const document = documentData[0];

            return {
              report_id: report.report_id,
              document: {
                upload_id: document.upload_id,
                file_url: document.file_url,
                name: `${studentName} Report`
              }
            };
          } catch (error) {
            return {
              report_id: report.report_id,
              error: `File upload failed: ${error.message}`
            };
          }
        });

      const uploadResults = await Promise.all(uploadPromises);

      const successfulUploads = uploadResults.filter(result => !result.error);
      const failedUploads = uploadResults.filter(result => result.error);

      if (failedUploads.length > 0) {
        // eslint-disable-next-line no-console
        console.error("Some reports failed to upload:", failedUploads);
        if (successfulUploads.length === 0) {
          throw new Error("All report uploads failed");
        }
      }

      const response = await httprequest.post("/api/v1/custom-reports/publish", {
        reports: successfulUploads
      });

      return {
        success: true,
        message: `${successfulUploads.length} reports published successfully`,
        failedReports: failedUploads,
        data: response.data
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Batch publish failed:", error);

      return {
        success: false,
        message: error.response?.data?.message || error.message
      };
    }
  };

  const handleBatchPrint = async (reportIds) => {
    try {
      const pdfUrls = [];

      // Process reports sequentially to avoid memory issues
      for (const reportId of reportIds) {
        try {
          const pdfBlob = await generateReportPDF(reportId);
          const pdfUrl = URL.createObjectURL(pdfBlob);

          // Find the report data to get the name
          const report = reportData.find(r => r.report_id === reportId);
          pdfUrls.push({
            url: pdfUrl,
            name: `${report?.last_name || "Unknown"}_${report?.first_name || "Student"}_report.pdf`
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Failed to generate PDF for report ${reportId}:`, error);
        }
      }

      setBatchPrintUrls(pdfUrls);
      setBatchPrintModalOpen(true);
    } catch (error) {
      toast.error(`Failed to prepare reports for printing: ${error.message}`);
    }
  };

  const handleConfirmBatchAction = async () => {
    try {
      if (selectedRows.length === 0) {
        toast.warning("Please select at least one report");

        return;
      }

      const reportIds = selectedRows.map(row => row.report_id);
      const toastId = toast.loading(`Processing 0 of ${selectedRows.length} reports...`);

      let successCount = 0;

      switch (batchAction) {
      case "publish":
      case "publish_send":
        const publishResults = await uploadAndPublishReport(selectedRows);

        if (publishResults.success) {
          successCount = selectedRows.length;

          if (batchAction === "publish_send") {
            await httprequest.post(`${REPORT_ENDPOINT}/send`, {
              report_ids: selectedRows.map(row => row.report_id)
            });
          }
        }
        break;

      case "print":
        await handleBatchPrint(reportIds);
        successCount = selectedRows.length;
        break;

      case "ready":
        const readyResponse = await httprequest.patch(
          `${MARK_REPORT_ENDPOINT}ready-for-publish`,
          { report_ids: reportIds }
        );
        toast.success(readyResponse.data.message);
        successCount = selectedRows.length;
        break;

      default:
        break;
      }

      // Final status
      if (successCount === selectedRows.length) {
        toast.update(toastId, {
          render: `Successfully processed all ${successCount} reports`,
          type: "success",
          isLoading: false,
          autoClose: 2000
        });
      } else if (successCount > 0) {
        toast.update(toastId, {
          render: `Processed ${successCount} of ${selectedRows.length} reports successfully`,
          type: "success",
          isLoading: false,
          autoClose: 2000
        });
      }

      setIsBatchModalOpen(false);
      setBatchAction(null);
      getAllReports(); // Refresh data
    } catch (error) {
      toast.error(`Batch operation failed: ${error.message}`);
    }
  };

  // Search functionality
  const handleSearch = (e) => {
    const { value } = e.target;

    if (!value) {
      setFilteredData(reportData);

      return;
    }

    const searchValue = value.toLowerCase();
    const searchTerms = searchValue.split(" ");

    const result = reportData.filter((report) => searchTerms.every(term => (report?.target_name && report.target_name.toLowerCase().includes(term)) ||
        (report?.class_name && report.class_name.toLowerCase().includes(term)) ||
        (report?.status && report.status.toLowerCase().includes(term))
    )
    );

    setFilteredData(result);
  };

  const columns = [
    {
      name: "Target",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Target</p>}
          {row.target_name}
        </>
      )
    },
    {
      name: "Class",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Class</p>}
          {row.class_name}
        </>
      )
    },
    {
      name: "Status",
      center: true,
      cell: (row) => (
        <p style={{ display: "flex", flexDirection: "column" }}>
          {isMobile && <p style={{ margin: 0, fontSize: "12px" }}>Status</p>}
          <Badge status={row.status} />
        </p>
      )
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <button className="table-action-icon" onClick={() => handleViewReport(row)}>
            <Icon icon="hugeicons:view" />
          </button>
          {row.status === "ready_for_publish" &&
            <button className="table-action-icon" onClick={() => handlePublishClick(row)}>
              <Icon icon="entypo:publish" />
            </button>
          }
          {row.status === "published" &&
            <button className="table-action-icon" onClick={() => handlePrintClick(row)}>
              <Icon icon="fluent:print-32-regular" />
            </button>
          }
        </div>
      )
    }
  ];

  useEffect(() => {
    getAllReports();
  }, []);

  return (
    <Layout children={
      <>
        <BreadCrumb
          link_one="/report/custom"
          link_one_name="Custom Reports"
          active={`Custom Report for ${reports?.class?.name}`}
          description="Fill in the details to create a Custom Report"
        />

        <div className="page-content margin-top-30">
          {isReportLoading ? (
            <PageLoader />
          ) : (
            <div>
              <h2 style={{ textAlign: "left" }}>{reports.title} | {reports?.class?.name}</h2>

              <Table
                data={filteredData}
                columns={columns}
                subHeader={true}
                pagination
                selectableRows
                onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                subHeaderComponent={
                  <div className="action_div">
                    <TableSearch
                      placeholder="Search here..."
                      searchTableFunc={handleSearch}
                    />
                    <SelectGroup
                      children={
                        <select onChange={handleBatchActionChange} value="">
                          <option value="">Batch Actions</option>
                          <option value="publish">Batch Publish Reports</option>
                          <option value="publish_send">Batch Publish and Send Reports</option>
                          <option value="print">Batch Print Reports</option>
                          <option value="ready">Batch Ready to Publish Reports</option>
                        </select>
                      }
                    />
                  </div>
                }
              />
            </div>
          )}
        </div>

        {/* Print Modal */}
        <Modal
          title="Print Report"
          subtitle={`Print report for ${selectedReport?.last_name || ""} ${selectedReport?.first_name || ""}`}
          isopen={printModalOpen}
          closeModal={() => {
            setPrintModalOpen(false);
            if (selectedReport?.pdfUrl) {
              URL.revokeObjectURL(selectedReport.pdfUrl);
            }
          }}
          modalContent={
            <div style={{ textAlign: "center", padding: "20px" }}>
              <p>Choose an action for this report:</p>
              <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "20px" }}>
                <Button
                  variant="primaryoutline"
                  buttonSize="medium"
                  color="btnfontprimary"
                  buttonName="View PDF"
                  onClick={() => {
                    if (selectedReport?.pdfUrl) {
                      window.open(selectedReport.pdfUrl, "_blank");
                      setPrintModalOpen(false);
                    }
                  }}
                />
                <Button
                  variant="primary"
                  buttonSize="medium"
                  color="btndefault"
                  buttonName="Download PDF"
                  onClick={async () => {
                    try {
                      const pdfBlob = await generateReportPDF(selectedReport.report_id);
                      const link = document.createElement("a");
                      const pdfUrl = URL.createObjectURL(pdfBlob);
                      link.href = pdfUrl;
                      link.download = `${selectedReport.last_name}_${selectedReport.first_name}_report.pdf`;
                      document.body.appendChild(link);
                      link.click();

                      setTimeout(() => {
                        document.body.removeChild(link);
                        URL.revokeObjectURL(pdfUrl);
                      }, 100);

                      setPrintModalOpen(false);
                    } catch (error) {
                      toast.error(`Failed to download PDF: ${error.message}`);
                    }
                  }}
                />
              </div>
            </div>
          }
          modalFooter={
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName="Print Now"
                onClick={() => {
                  if (selectedReport?.pdfUrl) {
                    const iframe = document.createElement("iframe");
                    iframe.style.display = "none";
                    iframe.src = selectedReport.pdfUrl;
                    document.body.appendChild(iframe);
                    iframe.onload = () => {
                      iframe.contentWindow.print();
                    };
                    setPrintModalOpen(false);
                  }
                }}
              />
            </div>
          }
        />

        {/* Publish Modal */}
        <Modal
          title="Publish Report"
          subtitle={`Publish report for ${reportToPublish?.last_name} ${reportToPublish?.first_name}`}
          isopen={publishModalOpen}
          closeModal={() => setPublishModalOpen(false)}
          modalFooter={
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                variant="primaryoutline"
                buttonSize="full"
                color="btnfontprimary"
                buttonName="Cancel"
                onClick={() => setPublishModalOpen(false)}
              />
              <Button
                variant="primary"
                buttonSize="full"
                isLoading={isPublishing}
                color="btndefault"
                buttonName="Confirm Publish"
                onClick={handlePublishReport}
              />
            </div>
          }
        />

        {/* Batch Action Modal */}
        <Modal
          title={`Confirm Batch ${batchAction?.replace(/_/g, " ")?.toUpperCase()}`}
          isopen={isBatchModalOpen}
          closeModal={() => {
            setIsBatchModalOpen(false);
            setBatchAction(null);
          }}
          modalContent={
            <div>
              <p>Are you sure you want to {batchAction?.replace(/_/g, " ")} {selectedRows?.length || 0} selected reports?</p>
              {selectedRows?.length > 0 && (
                <ul style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {(Array.isArray(selectedRows) ? selectedRows : []).slice(0, 5).map(row => (
                    <li key={row.report_id}>
                      {row.last_name}, {row.first_name} - {row.class_name}
                    </li>
                  ))}
                  {selectedRows?.length > 5 && <li>...and {selectedRows.length - 5} more</li>}
                </ul>
              )}
            </div>
          }
          modalFooter={
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                variant="primaryoutline"
                buttonSize="full"
                color="btnfontprimary"
                buttonName="Cancel"
                onClick={() => {
                  setIsBatchModalOpen(false);
                  setBatchAction(null);
                }}
              />
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName={`Confirm ${batchAction?.replace(/_/g, " ")}`}
                onClick={handleConfirmBatchAction}
              />
            </div>
          }
        />

        {/* Batch Print Modal */}
        <Modal
          title="Batch Print Reports"
          isopen={batchPrintModalOpen}
          closeModal={() => {
            setBatchPrintModalOpen(false);
            setTimeout(() => {
              batchPrintUrls.forEach(item => URL.revokeObjectURL(item.url));
              setBatchPrintUrls([]);
            }, 10000);
          }}
          modalContent={
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              <p>{batchPrintUrls.length} reports ready for download:</p>
              <ul style={{ marginTop: "10px" }}>
                {batchPrintUrls.map((item, index) => (
                  <li key={index} style={{ marginBottom: "5px" }}>
                    {item.name || `Report ${index + 1}`}
                  </li>
                ))}
              </ul>
            </div>
          }
          modalFooter={
            <div>
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName="Download All"
                onClick={() => {
                  batchPrintUrls.forEach((item, index) => {
                    setTimeout(() => {
                      const link = document.createElement("a");
                      link.href = item.url;
                      link.download = item.name || `report_${index + 1}.pdf`;
                      link.style.display = "none";
                      document.body.appendChild(link);
                      link.click();
                      setTimeout(() => {
                        document.body.removeChild(link);
                      }, 100);
                    }, index * 1000);
                  });

                  setBatchPrintModalOpen(false);
                  setTimeout(() => {
                    toast.success(`Initiated download of ${batchPrintUrls.length} reports`);
                  }, batchPrintUrls.length * 1000);
                }}
              />
            </div>
          }
        />

        {/* <ToastContainer /> */}
      </>
    }
    />
  );
};

export default CustomReport;
